import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react';
import debounce from 'lodash/debounce';
import {useSelector, useDispatch} from 'react-redux';
import {addToHistory, setHistory} from '../store/slices/coloringSlice';
import styled, {keyframes} from 'styled-components';
import {playClickSound, playErrorSound} from '../utils/sounds';
import './SVGRenderer.css';
import {useVibration} from '../hooks/useVibration';
import pako from 'pako';
import { PersistentManager } from "../utils/PersistentManager";
import { usePreventClickDuringInteraction } from '../hooks/usePreventClickDuringInteraction';
import { updateImageUsageAndPersist } from '../store/slices/categoriesSlice';
import { useParams } from 'react-router-dom';
import { 
  selectCurrentColor, 
  selectHistory, 
  selectCurrentStep, 
  selectCurrentHistoryStep
} from '../store/selectors/coloringSelectors';
import { AppDispatch } from '../store';
import { ImageUsageManager } from '../utils/ImageUsageManager';

// Expose completed colors to parent component
interface CompletedColors {
  [colorIdx: string]: boolean;
}

// Event for notifying completed colors
const COLOR_COMPLETED_EVENT = 'colorCompleted';
const ALL_COLORS_COMPLETED_EVENT = 'allColorsCompleted';
const COLOR_SELECTED_EVENT = 'colorSelected';

// Custom event for color completion
interface ColorCompletedEventDetail {
  colorIdx: string;
  isCompleted: boolean;
  completedColors: CompletedColors;
  allCompleted: boolean;
}

const AUTO_SAVE_INTERVAL = 5000; // 5 seconds

const HIGHLIGHT_BG_COLOR = "#d0d0d080";
const BLINK_DURATION = 3000; // 3 seconds for the blinking effect

const PLAY_SOUND = false;

// const MIN_REMAINING_COUNT = 50;

const colorChildrenRect = (el: SVGElement, color: string, setAsSolved: boolean, skipHighlightedElement: boolean) => {
    const rects = el.querySelectorAll<SVGElement>('rect, path');
    rects.forEach(rect => {
        if (!(skipHighlightedElement && rect.getAttribute('fill') === HIGHLIGHT_BG_COLOR)) {
            rect.setAttribute('fill', color);
            if (setAsSolved) {
                el.classList.add('hidden');
            } else {
                el.classList.remove('hidden');
            }
        }
    });
};

const SVGContainer = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
    background: transparent;
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  50% { width: 10px; }
  100% { width: 5px; }
`;

const ProgressLine = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 2px;
  background: #4CAF50;
  border: 2px dashed #4CAF50;
  animation: ${progressAnimation} ${AUTO_SAVE_INTERVAL}ms linear infinite;
  opacity: ${props => props.show ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 1000;
`;

// Fireworks animation
// const fireworksAnimation = keyframes`
//   0% { transform: translate(0, 0); opacity: 1; }
//   50% { opacity: 0.8; }
//   100% { transform: translate(var(--tx), var(--ty)); opacity: 0; }
// `;

// const Firework = styled.div`
//   position: absolute;
//   width: 5px;
//   height: 5px;
//   border-radius: 50%;
//   background: var(--color);
//   opacity: 0;
//   z-index: 2000;
//   animation: ${fireworksAnimation} 1s ease-out forwards;
// `;

const FireworksContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1500;
`;

// const disablePointerEventsOnText = (svgElement: SVGElement) => {
//     const texts = svgElement.querySelectorAll('text');
//     texts.forEach(text => {
//         text.setAttribute('pointer-events', 'none');
//     });
// };

const fixSvgStroke = (svgElement: SVGElement) => {
    const style = svgElement?.querySelector('style');
    if (!svgElement || !style) return;

    // Inject rules for better text rendering
    const rules = `
        .colored-tile { stroke-width: 0.3 !important; }
        text {
            vector-effect: non-scaling-stroke;
            text-rendering: geometricPrecision;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, monospace, sans-serif;
            font-size: 3px;
        }
    `;
    style.textContent += `\n${rules}`;

    // Process text elements
    const texts = svgElement.querySelectorAll('text');
    texts.forEach(text => {
        // Store original font size
        const originalFontSize = text.getAttribute('font-size');
        if (originalFontSize) {
            text.setAttribute('data-original-font-size', originalFontSize);
        }
        
        // Add transform-origin for better scaling
        text.setAttribute('transform-origin', 'center');
        
        // Set font family and size
        text.setAttribute('font-family', "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, monospace, sans-serif");
        text.setAttribute('font-size', '3px');
        
        // Ensure text is always on top
        text.style.pointerEvents = 'none';
        text.style.zIndex = '1000';
    });
};

const highlightedElements = (svg: SVGElement, colorIdx: string | undefined, highlightedColor: string) => {
    if (!svg || !colorIdx) return;
    
    // Find all regions that should be highlighted based on the color index
    const elements = svg.querySelectorAll<SVGElement>('g[data-color]:not(.hidden)');
    elements.forEach((el) => {
        const dataColorIdx = el.getAttribute('data-index');
        
        // If this element has the matching color index, highlight it
        if (dataColorIdx === colorIdx) {
            const rects = el.querySelectorAll<SVGElement>('rect, path');
            rects.forEach(rect => {
                // Save the original color if not already saved
                if (!rect.getAttribute('data-original-fill')) {
                    const originalFill = rect.getAttribute('fill') || 'transparent';
                    rect.setAttribute('data-original-fill', originalFill);
                }
                // Apply highlight color
                rect.setAttribute('fill', highlightedColor);
            });
        } else {
            // For non-matching elements, restore original color if they were previously highlighted
            const rects = el.querySelectorAll<SVGElement>('rect, path');
            rects.forEach(rect => {
                const originalFill = rect.getAttribute('data-original-fill');
                if (originalFill) {
                    rect.setAttribute('fill', originalFill);
                    rect.removeAttribute('data-original-fill');
                }
            });
        }
    });
};

const getTranslatedBBox = (el: SVGGraphicsElement): DOMRect => {
    const bbox = el.getBBox();
    const transform = el.getAttribute('transform');
    let dx = 0, dy = 0;

    if (transform) {
        const match = /translate\(\s*([-\d.]+)[ ,]?\s*([-\d.]+)?\s*\)/.exec(transform);
        if (match) {
            dx = parseFloat(match[1]);
            dy = parseFloat(match[2] || "0");
        }
    }

    return {
        x: bbox.x + dx,
        y: bbox.y + dy,
        width: bbox.width,
        height: bbox.height,
        top: bbox.y + dy,
        left: bbox.x + dx,
        right: bbox.x + dx + bbox.width,
        bottom: bbox.y + dy + bbox.height,
        toJSON: () => ({}) // for compatibility
    } as DOMRect;
};


// 🔵 New helper to find direct neighbors
const findDirectNeighbors = (element: SVGElement, svg: SVGElement): SVGElement[] => {
    const neighbors: SVGElement[] = [];
    const regionBBox = getTranslatedBBox(element as SVGGraphicsElement);
    const allRegions = svg.querySelectorAll<SVGElement>('g');

    allRegions.forEach(other => {
        if (other === element) return;
        const otherBBox = getTranslatedBBox(other as SVGGraphicsElement);

        const horizontallyClose =
            (regionBBox.x <= otherBBox.x + otherBBox.width) &&
            (regionBBox.x + regionBBox.width >= otherBBox.x);

        const verticallyClose =
            (regionBBox.y <= otherBBox.y + otherBBox.height) &&
            (regionBBox.y + regionBBox.height >= otherBBox.y);

        const closeEnough =
            horizontallyClose && verticallyClose &&
            (
                Math.abs(regionBBox.x - (otherBBox.x + otherBBox.width)) <= 1 ||
                Math.abs((regionBBox.x + regionBBox.width) - otherBBox.x) <= 1 ||
                Math.abs(regionBBox.y - (otherBBox.y + otherBBox.height)) <= 1 ||
                Math.abs((regionBBox.y + regionBBox.height) - otherBBox.y) <= 1
            );

        if (closeEnough) {
            neighbors.push(other);
        }
    });

    return neighbors;
};

// Create fireworks animation
const createFireworks = (container: HTMLDivElement, count: number = 50) => {
    // Clear any existing fireworks
    container.innerHTML = '';
    
    const colors = ['#FF5252', '#FFEB3B', '#2196F3', '#4CAF50', '#9C27B0', '#FF9800'];
    
    for (let i = 0; i < count; i++) {
        const firework = document.createElement('div');
        const size = Math.random() * 8 + 2;
        const originX = 50 + (Math.random() - 0.5) * 30;
        const originY = 50 + (Math.random() - 0.5) * 30;
        const tx = (Math.random() - 0.5) * 200;
        const ty = (Math.random() - 0.5) * 200;
        const delay = Math.random() * 1.5;
        const color = colors[Math.floor(Math.random() * colors.length)];
        
        firework.className = 'firework';
        firework.style.setProperty('--tx', `${tx}px`);
        firework.style.setProperty('--ty', `${ty}px`);
        firework.style.setProperty('--color', color);
        firework.style.width = `${size}px`;
        firework.style.height = `${size}px`;
        firework.style.left = `${originX}%`;
        firework.style.top = `${originY}%`;
        firework.style.animationDelay = `${delay}s`;
        
        container.appendChild(firework);
    }
};

interface SVGRendererProps {
    svgContent: string,
    imageId: string,
    highlightedColorIdx?: string
}

const SVGRenderer: React.FC<SVGRendererProps> = ({
    svgContent,
    imageId,
    highlightedColorIdx
}) => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const { preventClick } = usePreventClickDuringInteraction(300);
    const dispatch = useDispatch<AppDispatch>();
    const [coloredRegions, setColoredRegions] = useState<{ [key: string]: string }>({});
    const [showProgress, setShowProgress] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGElement | null>(null);
    const lastSavedState = useRef<{ [key: string]: string }>({});
    const {vibrate} = useVibration();

    // Use memoized selectors
    const currentColor_ = useSelector(selectCurrentColor);
    const currentColor = currentColor_ === 'eraser' ? 'transparent' : currentColor_;
    const history = useSelector(selectHistory);
    const currentStep = useSelector(selectCurrentStep);
    // Use the memoized current history step for better performance
    const currentHistoryStep = useSelector(selectCurrentHistoryStep);

    const [remainingCount, setRemainingCount] = useState<number>(0);
    const [, setTotalCount] = useState<number>(0);
    const [showFireworks, setShowFireworks] = useState<boolean>(false);
    const fireworksRef = useRef<HTMLDivElement>(null);
    
    // Track completed colors
    const [completedColors, setCompletedColors] = useState<CompletedColors>({});
    const [allColorsCompleted, setAllColorsCompleted] = useState<boolean>(false);
    
    // Track if history is loaded
    const [historyLoaded, setHistoryLoaded] = useState<boolean>(false);

    // Track the last selected color for blinking effect
    const [lastSelectedColorIdx, setLastSelectedColorIdx] = useState<string | undefined>();
    
    // Function to create a blink effect for regions of a specific color
    const blinkRegionsForColor = useCallback((svg: SVGElement, colorIdx: string) => {
      
      // First, remove any previous blink effects
      const existingBlinkingRegions = svg.querySelectorAll('.blink-region');
      existingBlinkingRegions.forEach(region => {
        region.classList.remove('blink-region');
      });

      // Don't blink if history isn't loaded yet
      if (!historyLoaded) {
          return;
      }

      // if(remainingCount<= 0 || remainingCount > MIN_REMAINING_COUNT){
      //     return;
      // }

      // Find all regions matching the color index
      const regionsToHighlight = svg.querySelectorAll(`g[data-index='${colorIdx}']:not(.hidden)`);
      
      // Add blink class to each region
      regionsToHighlight.forEach(region => {
        region.classList.add('blink-region');
      });
      
      // Schedule removal of the blink effect after the duration
      setTimeout(() => {
        const blinkingRegions = svg.querySelectorAll('.blink-region');
        blinkingRegions.forEach(region => {
          region.classList.remove('blink-region');
        });
      }, BLINK_DURATION);
    }, [historyLoaded]);

    // Update the count of remaining elements and track completed colors
    const updateRemainingCount = useCallback(() => {
        const svg = svgRef.current;
        if (!svg) return;

        const allRegions = svg.querySelectorAll('g[data-color]');
        const unfilledRegions = svg.querySelectorAll('g[data-color]:not(.hidden)');
        
        // Get all unique color indices in the SVG
        const allColorIndices = new Set<string>();
        allRegions.forEach(region => {
            const colorIdx = region.getAttribute('data-index');
            if (colorIdx) allColorIndices.add(colorIdx);
        });
        
        // Track remaining count for current color
        const unfilledCurrentRegions = svg.querySelectorAll(`g[data-color='${currentColor}']:not(.hidden)`);
        setTotalCount(allRegions.length);
        setRemainingCount(unfilledCurrentRegions.length);
        
        // Check completion status for each color
        const newCompletedColors: CompletedColors = {};
        let allCompleted = true;
        
        allColorIndices.forEach(colorIdx => {
            const regionsForThisColor = svg.querySelectorAll(`g[data-index='${colorIdx}']`);
            const unfilledRegionsForThisColor = svg.querySelectorAll(`g[data-index='${colorIdx}']:not(.hidden)`);
            
            const isColorCompleted = regionsForThisColor.length > 0 && unfilledRegionsForThisColor.length === 0;
            newCompletedColors[colorIdx] = isColorCompleted;
            
            // If any color is not completed, allCompleted will be false
            if (!isColorCompleted) allCompleted = false;
        });
        
        // Update completed colors state
        setCompletedColors(newCompletedColors);
        setAllColorsCompleted(allCompleted);
        
        // Dispatch event with completed colors information
        const colorCompletedEvent = new CustomEvent<ColorCompletedEventDetail>(COLOR_COMPLETED_EVENT, {
            detail: {
                colorIdx: currentColor,
                isCompleted: newCompletedColors[currentColor] || false,
                completedColors: newCompletedColors,
                allCompleted
            }
        });
        window.dispatchEvent(colorCompletedEvent);
        
        // If all regions are filled, show fireworks and mark as completed
        if (unfilledRegions.length === 0 && allRegions.length > 0) {
            setShowFireworks(true);
            if (fireworksRef.current) {
                createFireworks(fireworksRef.current);
                // Create another batch of fireworks after a delay
                setTimeout(() => {
                    if (fireworksRef.current) {
                        createFireworks(fireworksRef.current);
                    }
                }, 1500);
            }
            
            // If all colors are completed, dispatch the all completed event
            if (allCompleted && categoryId) {
                const allCompletedEvent = new CustomEvent(ALL_COLORS_COMPLETED_EVENT);
                window.dispatchEvent(allCompletedEvent);
                
                // Mark the image as completed in persistent storage
                ImageUsageManager.getInstance().markImageAsCompleted(categoryId, imageId);
            }
        } else {
            setShowFireworks(false);
        }
    }, [currentColor, categoryId, imageId]);

    const debouncedAutoSave = useCallback(() => {
        const saveWithDebounce = debounce(() => {
            if (imageId) { // Ensure imageId is present
                const saveData = {
                    imageId: imageId,
                    coloredRegions: coloredRegions,
                    history: history,
                    currentStep: currentStep,
                    completedColors: completedColors,
                    allColorsCompleted: allColorsCompleted
                };
                // Compress the data before saving
                const compressedData = pako.deflate(JSON.stringify(saveData));
                // Convert Uint8Array to a base64 string manually
                let base64Data = '';
                for (let i = 0; i < compressedData.length; i++) {
                    base64Data += String.fromCharCode(compressedData[i]);
                }
                // Use PersistentManager to save data
                PersistentManager.getInstance().setItem(`coloring_${imageId}`, btoa(base64Data));
                lastSavedState.current = { ...coloredRegions }; // Update lastSavedState
                
                // Update image usage in Redux (only when saving data)
                if (categoryId && Object.keys(coloredRegions).length > 0) {
                    // If all colors are completed, mark as completed
                    if (allColorsCompleted) {
                        ImageUsageManager.getInstance().markImageAsCompleted(categoryId, imageId);
                    } else {
                        dispatch(updateImageUsageAndPersist({ imageId, categoryId }));
                    }
                }
            }
            setShowProgress(false);
        }, 2000);
        
        return saveWithDebounce;
    }, [imageId, coloredRegions, history, currentStep, categoryId, dispatch, completedColors, allColorsCompleted]);

    useEffect(() => {
        // Trigger debounced auto-save whenever the persistent parts of the state change
        // Add a more robust check if history or currentStep actually changed from the last *saved* state
        // For now, this triggers on any change to them
        setShowProgress(true); // Show progress when a change is detected that will lead to a save
        const saveFunction = debouncedAutoSave();
        saveFunction();

        // Cleanup function for the debounce
        return () => {
            saveFunction.cancel();
        };
    }, [history, currentStep, debouncedAutoSave]);

    // 🔵 Updated handleRegionClick to include neighbor coloring
    const handleRegionClick = useCallback((element: SVGElement) => {
        const regionId = element.id;
        if (!regionId) return;
    
        const idx = element.getAttribute("data-index");
        if (highlightedColorIdx !== undefined && idx !== highlightedColorIdx) {
            vibrate('tap');
            if(PLAY_SOUND){
                playErrorSound();
            }
            return;
        }
    
        const svg = svgRef.current;
        if (!svg) return;
    
        const newColoredRegions = { ...coloredRegions };
        const visited = new Set<string>();
    
        const colorRegionAndNeighbors = (el: SVGElement, level: number) => {
            const id = el.id;
            if (!id || visited.has(id)) return;
            visited.add(id);
    
            const elIdx = el.getAttribute('data-index');
            if (highlightedColorIdx !== undefined && elIdx !== highlightedColorIdx) return;
    
            // Apply the color to the element
            const isSolved = currentColor !== 'transparent' && currentColor !== HIGHLIGHT_BG_COLOR;
            
            if (currentColor === 'transparent') {
                // If using eraser (transparent), remove the region from colored regions
                delete newColoredRegions[id];
                // And make sure the element is not hidden
                colorChildrenRect(el, 'transparent', false, false);
            } else {
                // Otherwise, apply the color and update the colored regions
                colorChildrenRect(el, currentColor, isSolved, false);
                newColoredRegions[id] = currentColor;
            }
    
            // If we're at the maximum depth, stop recursion
            if (level <= 0) return;

            // Find and color neighbors recursively
            const neighbors = findDirectNeighbors(el, svg);
            neighbors.forEach(neighbor => colorRegionAndNeighbors(neighbor, level - 1));
        };
    
        const level = 5; // 🛠 You can change this to >1 if you want deeper neighbors
        colorRegionAndNeighbors(element, level);
    
        // Update local state
        setColoredRegions(newColoredRegions);
        
        // Provide feedback
        vibrate('tap');
        if(PLAY_SOUND) {
            playClickSound();
        }
        
        // Update history in Redux
        dispatch(addToHistory({ coloredRegions: newColoredRegions }));
        
        // Show save progress indicator
        setShowProgress(true);
        
        // Update the remaining count
        updateRemainingCount();
        
        console.log('Updated coloredRegions:', newColoredRegions);
    }, [currentColor, dispatch, vibrate, coloredRegions, highlightedColorIdx, updateRemainingCount]);

    useEffect(() => {
        const loadSavedData = async () => {
            try {
                const savedData = await PersistentManager.getInstance().getItem(`coloring_${imageId}`);
                if (savedData) {
                    try {
                        // Convert base64 string back to Uint8Array
                        const binaryString = atob(savedData);
                        const len = binaryString.length;
                        const bytes = new Uint8Array(len);
                        for (let i = 0; i < len; i++) {
                            bytes[i] = binaryString.charCodeAt(i);
                        }
                        // Decompress the data when loading
                        const decompressedData = pako.inflate(bytes, { to: 'string' });
                        const { 
                            coloredRegions: savedRegions, 
                            history: savedHistory, 
                            currentStep: savedStep,
                            completedColors: savedCompletedColors,
                            allColorsCompleted: savedAllColorsCompleted
                        } = JSON.parse(decompressedData);
                        
                        setColoredRegions(savedRegions || {});
                        lastSavedState.current = { ...(savedRegions || {}) };
                        
                        // Load completed colors if available
                        if (savedCompletedColors) {
                            setCompletedColors(savedCompletedColors);
                        }
                        
                        if (savedAllColorsCompleted !== undefined) {
                            setAllColorsCompleted(savedAllColorsCompleted);
                        }
                        
                        if (savedHistory && savedHistory.length > 0) {
                            dispatch(setHistory({ 
                                history: savedHistory, 
                                currentStep: Math.min(savedStep, savedHistory.length - 1) 
                            }));
                        } else {
                            dispatch(addToHistory({ coloredRegions: savedRegions || {} }));
                        }
                        
                        // Update image usage status
                        if (Object.keys(savedRegions || {}).length > 0 && categoryId) {
                            // If all colors are completed, mark as completed
                            if (savedAllColorsCompleted) {
                                ImageUsageManager.getInstance().markImageAsCompleted(categoryId, imageId);
                            } else {
                                dispatch(updateImageUsageAndPersist({ imageId, categoryId }));
                            }
                        }
                    } catch (error) {
                        console.error('Error loading saved state:', error);
                        // Initialize with empty state on error
                        setColoredRegions({});
                        lastSavedState.current = {};
                        setCompletedColors({});
                        setAllColorsCompleted(false);
                        dispatch(setHistory({ history: [{ coloredRegions: {} }], currentStep: 0 }));
                    }
                } else {
                    // Clear state if no saved data
                    setColoredRegions({});
                    lastSavedState.current = {};
                    setCompletedColors({});
                    setAllColorsCompleted(false);
                    dispatch(setHistory({ history: [{ coloredRegions: {} }], currentStep: 0 }));
                }
            } catch (error) {
                console.error('Error loading saved data:', error);
                // Clear state if error
                setColoredRegions({});
                lastSavedState.current = {};
                setCompletedColors({});
                setAllColorsCompleted(false);
                dispatch(setHistory({ history: [{ coloredRegions: {} }], currentStep: 0 }));
            }
        };
        
        loadSavedData();
    }, [imageId, dispatch, categoryId]);

    const debouncedRegionClick = useMemo(() => debounce(handleRegionClick, 200, {
        leading: false,
        trailing: true
    }), [handleRegionClick]);

    // Initialize SVG and event listeners
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !svgContent) return;

        // Create SVG element if it doesn't exist
        if (!svgRef.current) {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = svgContent;
            const svgElement = tempDiv.querySelector('svg');
            if (svgElement) {
                svgRef.current = svgElement;
                container.appendChild(svgElement);
                
                // Initialize the counter after SVG is loaded
                setTimeout(() => {
                    updateRemainingCount();
                    // Mark history as loaded after SVG is initialized
                    setHistoryLoaded(true);
                }, 100);
            }
        } else {
            // SVG already exists, mark history as loaded
            setHistoryLoaded(true);
        }

        const svg = svgRef.current;
        if (!svg) return;

        // Create separate handlers for click and touch
        const handleClickInteraction = (e: MouseEvent) => {
            console.log('Click event:', e);
            if (preventClick) {
                const shouldPrevent = preventClick(e);
                console.log('Prevent click:', shouldPrevent);
                if (shouldPrevent) return; // click blocked due to interaction
            }
            
            e.preventDefault();
            const target = (e.target as SVGElement).closest("g[data-color]") as SVGElement;
            if (target && target.tagName === 'g') {
                debouncedRegionClick(target);
            }
        };
        
        // Variables to track touch
        let touchStartX = 0;
        let touchStartY = 0;
        let isColoring = false;
        const touchMoveTolerance = 5; // Pixels of movement allowed before it's considered a pan
        
        const handleTouchInteraction = (e: TouchEvent) => {
            console.log('Touch event:', e);
            
            // Only handle single touches for coloring
            if (e.touches.length !== 1) return;
            
            if (preventClick) {
                const shouldPrevent = preventClick(e);
                console.log('Prevent touch:', shouldPrevent);
                if (shouldPrevent) return; // touch blocked due to interaction
            }
            
            // Record the touch start position
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            
            const touch = e.touches[0];
            const target = document.elementFromPoint(touch.clientX, touch.clientY) as SVGElement;
            const region = target?.closest("g[data-color]") as SVGElement;
            
            if (region && region.tagName === 'g') {
                // Mark that we're going to color this region
                isColoring = true;
                
                // Prevent default to avoid browsers handling it differently
                e.preventDefault();
                // Don't stop propagation to allow ZoomableCanvas to detect this is a colorable region
            }
        };
        
        const handleTouchMove = (e: TouchEvent) => {
            if (!isColoring) return;
            
            // Calculate movement distance
            const moveX = Math.abs(e.touches[0].clientX - touchStartX);
            const moveY = Math.abs(e.touches[0].clientY - touchStartY);
            
            // If user moved significantly, they're probably trying to pan, not color
            if (moveX > touchMoveTolerance || moveY > touchMoveTolerance) {
                isColoring = false;
                return; // Let the parent handle panning
            } else {
                // Prevent default to avoid panning if we're still in coloring mode
                e.preventDefault();
                // Only stop propagation if we're definitely coloring
                e.stopPropagation();
            }
        };
        
        const handleTouchEnd = (e: TouchEvent) => {
            if (!isColoring) return;
            
            // Prevent default for the touch end event
            e.preventDefault();
            e.stopPropagation();
            
            // Get the element at the end position
            const touch = e.changedTouches[0];
            const target = document.elementFromPoint(touch.clientX, touch.clientY) as SVGElement;
            const region = target?.closest("g[data-color]") as SVGElement;
            
            if (region && region.tagName === 'g') {
                debouncedRegionClick(region);
            }
            
            // Reset coloring state
            isColoring = false;
        };

        // disablePointerEventsOnText(svg);
        fixSvgStroke(svg);

        // Add separate event listeners for click and touch
        svg.addEventListener('click', handleClickInteraction as EventListener);
        svg.addEventListener('touchstart', handleTouchInteraction as EventListener, {passive: false});
        svg.addEventListener('touchmove', handleTouchMove as EventListener, {passive: false});
        svg.addEventListener('touchend', handleTouchEnd as EventListener, {passive: false});

        // Set touch-action on SVG to manipulate
        svg.style.touchAction = 'manipulation';
        
        // Set pointer-events to all and ensure elements are clickable
        svg.style.pointerEvents = 'all';
        const colorGroups = svg.querySelectorAll('g[data-color]');
        colorGroups.forEach(group => {
            (group as HTMLElement).style.pointerEvents = 'all';
            (group as HTMLElement).style.cursor = 'pointer';
        });
        
        // Cleanup function
        return () => {
            svg.removeEventListener('click', handleClickInteraction as EventListener);
            svg.removeEventListener('touchstart', handleTouchInteraction as EventListener);
            svg.removeEventListener('touchmove', handleTouchMove as EventListener);
            svg.removeEventListener('touchend', handleTouchEnd as EventListener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [svgContent, debouncedRegionClick, updateRemainingCount]);

    // Handle undo/redo
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        
        // Use the memoized currentHistoryStep for better performance
        const newColoredRegions = currentHistoryStep.coloredRegions || {};
        setColoredRegions(newColoredRegions);

        // First, reset all regions to their default state
        const allRegions = svg.querySelectorAll('g[data-color]');
        allRegions.forEach(region => {
            // Remove any temporary classes
            region.classList.remove('temp-history', 'hidden');
            // Reset fill to transparent
            colorChildrenRect(region as SVGElement, 'transparent', false, true);
        });

        // Then apply colors from the current history step
        Object.entries(newColoredRegions).forEach(([regionId, color]) => {
            const element = svg.querySelector(`#${regionId}`) as SVGElement;
            if (element) {
                // Skip elements with transparent color (they're already reset)
                if (color === 'transparent' || color === '#ffffff') {
                    return;
                }
                
                // Apply the color and set the element as solved if it's not transparent
                const isSolved = color !== 'transparent' && color !== HIGHLIGHT_BG_COLOR;
                colorChildrenRect(element, color, isSolved, false);
            }
        });
        
        // Apply highlighting for the currently selected color index
        if (highlightedColorIdx) {
            highlightedElements(svg, highlightedColorIdx, HIGHLIGHT_BG_COLOR);
        }
        
        // Update the remaining count after applying colors
        updateRemainingCount();
        
        // Mark history as loaded
        setHistoryLoaded(true);
    }, [currentHistoryStep, highlightedColorIdx, updateRemainingCount]);

    // Apply highlighting when the highlighted color index changes
    useEffect(() => {
        if (!highlightedColorIdx) return;
        
        const svg = svgRef.current;
        if (!svg) return;
        
        highlightedElements(svg, highlightedColorIdx, HIGHLIGHT_BG_COLOR);
        
    }, [highlightedColorIdx, currentStep]);

    // Expose remaining count and completed colors to global window object for parent component to access
    useEffect(() => {
        // @ts-ignore
        window._remainingCount = remainingCount;
        // @ts-ignore
        window._completedColors = completedColors;
        // @ts-ignore
        window._allColorsCompleted = allColorsCompleted;
    }, [remainingCount, completedColors, allColorsCompleted]);

    // Listen for color selection event
    useEffect(() => {
      const handleColorSelected = (e: CustomEvent<{ colorIdx: string }>) => {
        const { colorIdx } = e.detail;
        if (colorIdx && svgRef.current && historyLoaded) {
          setLastSelectedColorIdx(colorIdx);
          blinkRegionsForColor(svgRef.current, colorIdx);
        }
      };
      
      window.addEventListener(COLOR_SELECTED_EVENT, handleColorSelected as EventListener);
      
      return () => {
        window.removeEventListener(COLOR_SELECTED_EVENT, handleColorSelected as EventListener);
      };
    }, [blinkRegionsForColor, historyLoaded]);
    
    // Blink regions when highlighted color changes
    useEffect(() => {
      if (highlightedColorIdx && svgRef.current && highlightedColorIdx !== lastSelectedColorIdx && historyLoaded) {
        setLastSelectedColorIdx(highlightedColorIdx);
        blinkRegionsForColor(svgRef.current, highlightedColorIdx);
      }
    }, [highlightedColorIdx, lastSelectedColorIdx, blinkRegionsForColor, historyLoaded]);
    
    // Add CSS for the blink effect
    useEffect(() => {
      const style = document.createElement('style');
      style.innerHTML = `
        .blink-region {
          animation: blink 0.5s ease-in-out infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `;
      document.head.appendChild(style);
      
      return () => {
        document.head.removeChild(style);
      };
    }, []);

    return (
        <SVGContainer>
            <ProgressLine show={showProgress}/>
            <div ref={containerRef} className="svg-container"/>
            {showFireworks && (
                <FireworksContainer ref={fireworksRef} />
            )}
        </SVGContainer>
    );
};

export default SVGRenderer;
